'use client';

import {
    ArrowDownIcon,
    ArrowUpIcon,
    SparklesIcon,
} from '@heroicons/react/20/solid';
import { useState } from 'react';
import { TextArea } from '../atoms/TextAreaAutoSize';
import { useEditor } from '../context/EditorContext/EditorContext';

export default function PromptInput({
    label,
    file,
    componentName,
    element: key,
}) {
    const editor = useEditor();
    const loading = editor.components.generating({ file, key });

    const [open, setOpen] = useState(false);
    const [prompt, setPrompt] = useState('');
    const [include_page_context, setIncludePageContext] = useState(false);
    const [temperature, setTemperature] = useState(0.5);

    const getSliderAccent = () => {
        switch (temperature) {
            case 0:
                return 'accent-[#842c86]';
            case 0.1:
                return 'accent-[#973385]';
            case 0.2:
                return 'accent-[#ae397f]';
            case 0.3:
                return 'accent-[#c6407c]';
            case 0.4:
                return 'accent-[#d74c6c]';
            case 0.5:
                return 'accent-[#e85868]';
            case 0.6:
                return 'accent-[#f56e5f]';
            case 0.7:
                return 'accent-[#f98565]';
            case 0.8:
                return 'accent-[#fc9f6f]';
            case 0.9:
                return 'accent-[#fbb87f]';
            case 1:
                return 'accent-[#fed094]';
            default:
                return 'accent-[#e85868]';
        }
    };

    return (
        <div>
            <div className="mt-4 flex items-center justify-between">
                {label}
                <div className="flex items-center ">
                    <button
                        type="button"
                        className="focus-visible:transparent mr-1 flex h-4 w-4 items-center justify-center rounded-full text-neutral-800 transition-all ease-in hover:text-green-400"
                        onClick={() => {
                            setOpen(!open);
                            setPrompt('');
                        }}
                    >
                        {open ? (
                            <ArrowUpIcon className="h-5 w-5" />
                        ) : (
                            <ArrowDownIcon className="h-5 w-5" />
                        )}
                    </button>

                    <button
                        type="button"
                        className="focus-visible:transparent mr-1 flex h-4 w-4 items-center justify-center rounded-full text-neutral-800 transition-all ease-in hover:text-green-400"
                        disabled={loading}
                        onClick={() =>
                            editor.components.generate({
                                file,
                                component_name: componentName,
                                key,
                                prompt,
                                temperature,
                                use_gpt4: true, // ← always true
                                include_page_context,
                            })
                        }
                    >
                        {loading ? (
                            <svg
                                aria-hidden="true"
                                className="inline h-5 w-5 animate-spin fill-green-500"
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" />
                            </svg>
                        ) : (
                            <SparklesIcon className="h-5 w-5" />
                        )}
                    </button>
                </div>
            </div>

            {open && (
                <div className="mb-2 flex flex-col gap-2">
                    <TextArea
                        className="block w-full rounded-md border-0 py-1.5 text-xs leading-5 text-neutral-800 ring-1 ring-inset ring-gray-200 placeholder:text-neutral-400 focus:ring-1 focus:ring-inset focus:ring-blue-500"
                        placeholder="Enter prompt here..."
                        value={prompt}
                        onChange={(e) => setPrompt(e.target.value)}
                    />

                    <label className="relative inline-flex cursor-pointer items-center">
                        <input
                            type="checkbox"
                            className="peer sr-only"
                            checked={include_page_context}
                            onChange={() =>
                                setIncludePageContext(!include_page_context)
                            }
                        />
                        <div className="peer h-4 w-7 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-0.5 after:h-3 after:w-3 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-1 peer-focus:ring-blue-300" />
                        <span className="ml-3 text-xs font-medium text-gray-900">
                            Include Page Components in Context
                        </span>
                    </label>

                    <label
                        htmlFor="minmax-range"
                        className="block text-sm font-medium text-gray-900"
                    >
                        Temperature {temperature}
                    </label>
                    <input
                        id="minmax-range"
                        type="range"
                        min="0"
                        max="1"
                        step="0.1"
                        value={temperature}
                        onChange={(e) =>
                            setTemperature(Number.parseFloat(e.target.value))
                        }
                        className={`mb-2 h-2 w-full cursor-pointer appearance-none rounded-lg bg-neutral-200 ${getSliderAccent()}`}
                    />
                </div>
            )}
        </div>
    );
}
